import { Activity } from "@/components/icons";
import type { TradingCluster } from "../api/types";
import { clockTime } from "../components/format";
import { ClusterCard } from "./ClusterCard";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { SeverityThresholds } from "./types";
import { cn } from "@/lib/utils";

interface FreshWallProps {
  clusters: TradingCluster[];
  since: string | null;
  expandedClusterIds: Set<string>;
  selectedItemId: string | null;
  highlightedItemId: string | null;
  muted: boolean;
  thresholds: SeverityThresholds;
  now: number;
  materialId: string;
  otherMaterialId: string;
  notesCountsMap?: Record<string, number>;
  onToggleCluster: (clusterId: string) => void;
  onOpenArticle: (articleId: string, siblingIds: string[]) => void;
  onJumpToOtherPane: (clusterId: string, materialId: string) => void;
  onOpenClusterNotes?: (clusterId: string, title: string) => void;
}

export function FreshWall({
  clusters,
  since,
  expandedClusterIds,
  selectedItemId,
  highlightedItemId,
  muted,
  thresholds,
  now,
  materialId,
  otherMaterialId,
  notesCountsMap,
  onToggleCluster,
  onOpenArticle,
  onJumpToOtherPane,
  onOpenClusterNotes,
}: FreshWallProps) {
  if (clusters.length === 0) {
    return (
      <section
        className="m-3 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/20 p-8 text-sm text-muted-foreground"
        aria-label="Fresh events"
      >
        <Activity className="h-5 w-5 text-primary/50" />
        <div>No new events since {clockTime(since)} · waiting.</div>
      </section>
    );
  }

  return (
    <section
      className={cn(
        "m-3 flex flex-col rounded-lg border border-dashed border-border/60 bg-muted/20",
        muted && "opacity-60",
      )}
      aria-label="Fresh events"
    >
      <div className="sticky top-0 z-[1] flex items-center gap-2 rounded-t-lg border-b border-border/60 bg-muted/40 px-3 py-1.5 text-sm backdrop-blur-sm">
        <Tooltip>
          <TooltipTrigger asChild>
            <strong className="cursor-help text-amber-500">Fresh wall</strong>
          </TooltipTrigger>
          <TooltipContent>
            New news since the last analysis. Not yet incorporated into the current analysis.
          </TooltipContent>
        </Tooltip>
        <span className="text-muted-foreground text-xs">since {clockTime(since)}</span>
      </div>
      <div className="flex flex-col gap-2 p-3">
        {clusters.map((cluster) => (
          <ClusterCard
            cluster={cluster}
            expanded={expandedClusterIds.has(cluster.cluster_id)}
            key={cluster.cluster_id}
            highlighted={highlightedItemId === cluster.cluster_id}
            materialId={materialId}
            now={now}
            notesCount={notesCountsMap?.[`cluster:${cluster.cluster_id}`] ?? 0}
            onJumpToOtherPane={onJumpToOtherPane}
            onOpenArticle={onOpenArticle}
            onOpenNotes={onOpenClusterNotes ? () => onOpenClusterNotes(cluster.cluster_id, cluster.representative_title) : undefined}
            onToggle={onToggleCluster}
            otherMaterialId={otherMaterialId}
            selected={selectedItemId === cluster.cluster_id}
            thresholds={thresholds}
            variant="fresh"
          />
        ))}
      </div>
    </section>
  );
}
